/**
 * The tracker sheet, as a screen.
 *
 * One row per *submission*, not per placement. The workbook tracked a person
 * from the moment they were put forward to a client, and a row that only
 * appears once somebody has joined cannot be the sheet the recruiters worked
 * from — the rows they chase hardest are the ones with no joining date yet.
 *
 * Columns in the workbook's own order: Sr-No, Recruiter, Month, Candidate,
 * Mobile, Client, Location, Position, DOI, DOJ, CTC, Status, Remarks.
 *
 * Status is derived by the server across submission, interview, offer and
 * placement — see `app/services/tracker.py` for the ladder. Nothing here
 * recomputes it, because a status derived twice is a status that disagrees
 * with itself.
 *
 * Two columns fill in only once an offer is accepted: DOJ and CTC live on the
 * placement. A row with neither is not incomplete, it is early.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, Loader2, Pencil, Table2 } from 'lucide-react';
import { toast } from 'sonner';
import { client as api, errorMessage } from '../api/client';
import { downloadExport, exportErrorMessage } from '../lib/exportDownload';
import { money } from '../lib/money';
import { formatPhone } from '../lib/phone';
import { isSupervisor, useAuth } from '../store/auth';
import type {
  Client,
  FeeBasis,
  Page,
  PaymentState,
  PlacementStatus,
  TrackerRow,
  TrackerStatus,
  User,
} from '../types';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];


/** Six years back is further than this data goes and cheap to offer. */
const YEARS = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

/**
 * Row fill by status, matching the Excel export's scheme.
 *
 * Taken from STATUS_FILLS in `xlsx_export.py` — grouped by what the reader does
 * next rather than by which enum the value came from. Green is progressing,
 * amber is waiting on somebody, red is closed and the answer was no.
 *
 * The five rungs the export did not have when it was written are coloured on
 * the same rule and added there in the same change, so the sheet and the file
 * stay one scheme: an offer out is progress, an interview not yet held is
 * waiting, and a refused offer is closed.
 *
 * This deliberately disagrees with the badge palette on Placements, which
 * colours `hold` grey. The export calls it amber, because a hold is somebody
 * still to come back to rather than a dead end, and the brief for this screen
 * was to match the export. Placements keeps its own scheme.
 *
 * Colour is never the only signal — Status stays in its own column, which is
 * what keeps the screen readable to the ~8% of men with red/green colour
 * blindness.
 */
/** Four words for the money, and colour is never the only signal. */
const PAYMENT_LABEL: Record<PaymentState, string> = {
  not_invoiced: 'Yet to pay',
  yet_to_receive: 'Invoice done',
  part_paid: 'Awaiting payment',
  received: 'Payment received',
  // Its own word. A withdrawn claim is not money that is late, and calling it
  // awaited would have the tracker wait for something nobody will send.
  cancelled: 'Cancelled',
};

const PAYMENT_TONE: Record<PaymentState, string> = {
  // Grey, not amber. Nothing is owed yet, so nothing is waiting.
  not_invoiced: 'bg-gray-100 text-gray-500',
  yet_to_receive: 'bg-amber-100 text-amber-800',
  // Amber too: part of it is still outstanding, and that is the fact worth
  // seeing. The label is what separates the two.
  part_paid: 'bg-amber-100 text-amber-800',
  received: 'bg-emerald-100 text-emerald-700',
  // A withdrawn claim is not money that is late; it is money nobody will send.
  cancelled: 'bg-gray-100 text-gray-400',
};

const ROW_TONE: Record<TrackerStatus, string> = {
  // Progressing.
  shortlisted: 'bg-emerald-50',
  interviewed: 'bg-emerald-50',
  selected: 'bg-emerald-50',
  offered: 'bg-emerald-50',
  working: 'bg-emerald-50',
  // Waiting on somebody.
  pending: 'bg-amber-50',
  interview_scheduled: 'bg-amber-50',
  followup: 'bg-amber-50',
  hold: 'bg-amber-50',
  yet_to_join: 'bg-amber-50',
  // Closed, and the answer was no.
  rejected: 'bg-red-50',
  offer_declined: 'bg-red-50',
  offer_withdrawn: 'bg-red-50',
  drop: 'bg-red-50',
  not_joined: 'bg-red-50',
  // Closed, and the answer was yes in the end. A drop we replaced kept the
  // fee, so colouring it with the drops would read as a loss it was not.
  replacement_done: 'bg-emerald-50',
};

/**
 * No column is frozen any more.
 *
 * The two FROZEN_* class strings that stood here described a sticky Candidate
 * column and stopped being referenced when that column was unfrozen. They
 * were left behind, and `noUnusedLocals` in tsconfig.app.json turns an unused
 * const into a build failure rather than a warning — so the leftovers broke
 * the production build while every local check passed.
 */

const STATUS_TEXT: Record<TrackerStatus, string> = {
  shortlisted: 'text-emerald-800',
  interviewed: 'text-emerald-800',
  selected: 'text-emerald-800',
  offered: 'text-emerald-800',
  working: 'text-emerald-800',
  pending: 'text-amber-800',
  interview_scheduled: 'text-amber-800',
  followup: 'text-amber-800',
  hold: 'text-amber-800',
  yet_to_join: 'text-amber-800',
  rejected: 'text-red-800',
  offer_declined: 'text-red-800',
  offer_withdrawn: 'text-red-800',
  drop: 'text-red-800',
  not_joined: 'text-red-800',
  replacement_done: 'text-emerald-800',
};

/** Every rung of the ladder, in funnel order not alphabetical. */
const TRACKER_STATUSES: TrackerStatus[] = [
  'pending',
  'shortlisted',
  'interview_scheduled',
  'interviewed',
  'selected',
  'followup',
  'offered',
  'offer_declined',
  'offer_withdrawn',
  'yet_to_join',
  'working',
  'hold',
  'rejected',
  'drop',
  'not_joined',
];

/** What a placement's own status may be set to, which is a shorter list. */
const PLACEMENT_STATUSES: PlacementStatus[] = [
  'yet_to_join',
  'working',
  'hold',
  'not_joined',
  'drop',
  'rejected',
];

/** An empty cell that reads as empty rather than as a missing column. */
function Blank() {
  return <span className="text-gray-300">—</span>;
}

/** '' from an empty input means "no value", which the API spells as null. */
function orNull(value: string): string | null {
  return value.trim() === '' ? null : value.trim();
}

function label(status: string): string {
  return status.replace(/_/g, ' ');
}

/**
 * The month a row files under.
 *
 * Split off the ISO string rather than parsed into a Date. `new Date('2026-03-01')`
 * is midnight UTC, which in a timezone behind UTC is still February — so a row
 * submitted on the 1st would file under the previous month for anyone west of
 * Greenwich. The server groups by the same string.
 */

/**
 * Correct the placement behind a tracker row.
 *
 * Only offered on rows that have one. Every field here lives on the placement,
 * so on a row that has not got that far there is nothing for this to write to
 * — the control is absent rather than present-and-failing.
 *
 * No role gate. PATCH /placements is scoped by visibility rather than by role:
 * a recruiter may correct their own rows, a manager their subtree. That
 * matches the offer flow, where accepting an offer — the act that creates the
 * placement in the first place — is open to any signed-in user. Requiring a
 * manager to fix a row would be stricter than making it.
 *
 * Remarks is here too, and is the one field that does not live on the
 * placement — it is the supervisor's note on the submission, still editable in
 * place on the row. Having it in both is the point: a manager fixing a row
 * should not have to close this and go hunting for one column.
 *
 * It is also the one field a recruiter cannot write. The pencil is gated on a
 * row having a placement rather than on rank, so a recruiter can open this and
 * correct their own placement fields — but the remarks route is Approver, so
 * for them this shows the manager's note and refuses to be typed in. A field
 * that accepts text and then 403s on save would be worse than one that says
 * plainly it is not theirs.
 */
function EditDialog({
  row,
  saving,
  canEditRemarks,
  onCancel,
  onSave,
}: {
  row: TrackerRow;
  saving: boolean;
  canEditRemarks: boolean;
  onCancel: () => void;
  onSave: (
    payload: Record<string, unknown>,
    remarks: { changed: boolean; value: string },
  ) => void;
}) {
  const [remarks, setRemarks] = useState(row.trackerRemarks ?? '');
  const [position, setPosition] = useState(row.position ?? '');
  const [location, setLocation] = useState(row.location ?? '');
  const [dateOfInterview, setDateOfInterview] = useState(row.dateOfInterview ?? '');
  const [dateOfJoining, setDateOfJoining] = useState(row.dateOfJoining ?? '');
  const [ctcAnnual, setCtcAnnual] = useState(row.ctcAnnual ?? '');
  const [billingAmount, setBillingAmount] = useState(row.billingAmount ?? '');
  const [feeBasis, setFeeBasis] = useState<FeeBasis | ''>(row.feeBasis ?? '');
  const [feePercent, setFeePercent] = useState(row.feePercent ?? '');
  const [guaranteeUntil, setGuaranteeUntil] = useState(row.guaranteeUntil ?? '');

  /**
   * The client's standing terms, so the blanks can be offered rather than
   * calculated by hand.
   *
   * Fetched when the dialog opens instead of being carried on every tracker
   * row: one client's terms are the same for all their rows, and putting four
   * more columns on a paged list to serve one dialog is the wrong trade.
   */
  const client = useQuery<Client>({
    queryKey: ['client', row.clientId],
    queryFn: async () => (await api.get(`/api/v1/clients/${row.clientId}`)).data,
    staleTime: 5 * 60_000,
  });

  /**
   * What the client's terms say this placement's fee would be.
   *
   * A *suggestion*, and it is important that it is only ever that. Nothing
   * writes it: it fills the box, the person looking at the box decides, and
   * pressing Save is what makes it the agreed fee. That is the line
   * offer_service draws — "a copied-but-stale figure is worse than a blank
   * one because it looks settled" — and it holds here because a human is
   * looking at a form, which is exactly what is absent when a placement is
   * created by accepting an offer.
   *
   * Null when there is nothing honest to suggest: no standing terms, or a
   * percentage basis with no CTC to take a percentage of. An empty box beats
   * a figure derived from a blank.
   */
  const suggestedFee = (() => {
    const c = client.data;
    if (!c || billingAmount.trim() !== '') return null;
    if (c.defaultFeeBasis === 'flat') return c.defaultBillingAmount;
    if (c.defaultFeeBasis === 'ctc_percent' && c.defaultFeePercent) {
      const ctc = Number(ctcAnnual);
      const pct = Number(c.defaultFeePercent);
      if (!Number.isFinite(ctc) || ctc <= 0 || !Number.isFinite(pct)) return null;
      return ((ctc * pct) / 100).toFixed(2);
    }
    return null;
  })();

  /** The guarantee window the client's replacement period implies. */
  const suggestedGuarantee = (() => {
    const days = client.data?.defaultReplacementDays;
    if (!days || guaranteeUntil.trim() !== '' || dateOfJoining.trim() === '') {
      return null;
    }
    const joined = new Date(dateOfJoining);
    if (Number.isNaN(joined.getTime())) return null;
    return new Date(joined.getTime() + days * 86_400_000)
      .toISOString()
      .slice(0, 10);
  })();
  // Safe to narrow: on a placement-backed row the derived status is the
  // placement's own, which is always one of the six below.
  const [status, setStatus] = useState<PlacementStatus>(
    row.status as PlacementStatus,
  );

  const statusChanged = status !== row.status;

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 p-4">
      <form
        className="card max-h-[90vh] w-full max-w-lg space-y-3 overflow-y-auto"
        onSubmit={(e) => {
          e.preventDefault();
          onSave(
            {
              position: orNull(position),
              location: orNull(location),
              dateOfInterview: orNull(dateOfInterview),
              dateOfJoining: orNull(dateOfJoining),
              ctcAnnual: orNull(ctcAnnual),
              billingAmount: orNull(billingAmount),
              feeBasis: feeBasis || null,
              feePercent: orNull(feePercent),
              guaranteeUntil: orNull(guaranteeUntil),
              status,
            },
            // Sent separately, and only when it actually changed — the note
            // lives on the submission and has its own route, so an untouched
            // dialog stays a single request.
            {
              changed:
                canEditRemarks && remarks.trim() !== (row.trackerRemarks ?? ''),
              value: remarks.trim(),
            },
          );
        }}
      >
        <h3 className="text-sm font-semibold text-gray-900">
          {row.candidateName ?? 'Placement'}
          {row.clientName && (
            <span className="ml-1.5 font-normal text-gray-500">
              at {row.clientName}
            </span>
          )}
        </h3>
        <p className="text-xs text-gray-500">
          Most of these live on the placement. Recruiter, client, candidate and
          mobile are fixed or belong to the candidate record. Remarks is the
          supervisor&rsquo;s note on the submission and is also editable in
          place on the row.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-gray-600">
            Position
            <input
              spellCheck={false}
              className="input mt-1"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              maxLength={160}
            />
          </label>

          <label className="text-xs text-gray-600">
            Location
            <input
              spellCheck={false}
              className="input mt-1"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              maxLength={120}
              placeholder="Hyderabad"
            />
          </label>

          <label className="text-xs text-gray-600">
            DOI
            <input
              className="input mt-1"
              type="date"
              value={dateOfInterview}
              onChange={(e) => setDateOfInterview(e.target.value)}
            />
            <span className="mt-0.5 block text-[11px] text-gray-400">
              Date of interview
            </span>
          </label>

          <label className="text-xs text-gray-600">
            DOJ
            <input
              className="input mt-1"
              type="date"
              value={dateOfJoining}
              onChange={(e) => setDateOfJoining(e.target.value)}
            />
            <span className="mt-0.5 block text-[11px] text-gray-400">
              Date of joining
            </span>
          </label>

          <label className="text-xs text-gray-600">
            CTC
            <input
              spellCheck={false}
              className="input mt-1"
              inputMode="decimal"
              value={ctcAnnual}
              onChange={(e) => setCtcAnnual(e.target.value)}
              placeholder="450000"
            />
            <span className="mt-0.5 block text-[11px] text-gray-400">
              Annual, in rupees
            </span>
          </label>

          {/* The agreed fee.
              Accepting an offer leaves it unset on purpose — the posting's
              rate is what was advertised, not what was agreed — and until
              this box existed nothing could fill it in afterwards. That one
              blank is what makes a row show no incentive (4% of this) and
              show no Raise button (billing refuses with "No agreed fee on the
              placement"), so it is the field that quietly stops the chain. */}
          <label className="text-xs text-gray-600">
            Agreed Fee
            <input
              spellCheck={false}
              className="input mt-1"
              inputMode="decimal"
              value={billingAmount}
              onChange={(e) => setBillingAmount(e.target.value)}
              placeholder="21000"
            />
            {/* Offered, not applied. One click puts it in the box; Save is
                what makes it the agreed fee. */}
            {suggestedFee !== null ? (
              <button
                type="button"
                className="mt-1 text-left text-[11px] text-indigo-600 hover:underline"
                onClick={() => setBillingAmount(suggestedFee)}
              >
                Use {money(suggestedFee)} — this client&rsquo;s agreed{' '}
                {client.data?.defaultFeeBasis === 'flat'
                  ? 'flat fee'
                  : `${client.data?.defaultFeePercent}% of CTC`}
              </button>
            ) : (
              <span className="mt-0.5 block text-[11px] text-gray-400">
                What the client pays, in rupees. The incentive is 4% of this.
              </span>
            )}
          </label>

          <label className="text-xs text-gray-600">
            Fee Basis
            <select
              className="input mt-1"
              value={feeBasis}
              onChange={(e) => setFeeBasis(e.target.value as FeeBasis | '')}
            >
              <option value="">Not recorded</option>
              <option value="flat">Flat</option>
              <option value="ctc_percent">% of CTC</option>
            </select>
            <span className="mt-0.5 block text-[11px] text-gray-400">
              How the fee was reached. Recorded, not applied.
            </span>
          </label>

          {/* Only meaningful alongside a percentage basis, and hidden rather
              than disabled on a flat fee: a box that cannot affect anything is
              a question somebody has to work out the answer to. */}
          {feeBasis === 'ctc_percent' && (
            <label className="text-xs text-gray-600">
              Fee %
              <input
                spellCheck={false}
                className="input mt-1"
                inputMode="decimal"
                value={feePercent}
                onChange={(e) => setFeePercent(e.target.value)}
                placeholder="8.33"
              />
              <span className="mt-0.5 block text-[11px] text-gray-400">
                Explains the fee above; it does not calculate it.
              </span>
            </label>
          )}

          {/* The window a drop inside voids the fee in.
              The invoice form has warned on this since it was written; until
              the client carried a replacement period, nothing ever set it. */}
          <label className="text-xs text-gray-600">
            Guarantee Until
            <input
              className="input mt-1"
              type="date"
              value={guaranteeUntil}
              onChange={(e) => setGuaranteeUntil(e.target.value)}
            />
            {suggestedGuarantee !== null ? (
              <button
                type="button"
                className="mt-1 text-left text-[11px] text-indigo-600 hover:underline"
                onClick={() => setGuaranteeUntil(suggestedGuarantee)}
              >
                Use {suggestedGuarantee} — {client.data?.defaultReplacementDays} days
                from joining
              </button>
            ) : (
              <span className="mt-0.5 block text-[11px] text-gray-400">
                Replacement guarantee window.
              </span>
            )}
          </label>

          <label className="text-xs text-gray-600">
            Status
            <select
              className="input mt-1"
              value={status}
              onChange={(e) => setStatus(e.target.value as PlacementStatus)}
            >
              {PLACEMENT_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {label(value)}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs text-gray-600 sm:col-span-2">
            Remarks
            <textarea
              spellCheck={true}
              className="input mt-1 block w-full"
              rows={2}
              maxLength={2000}
              value={remarks}
              readOnly={!canEditRemarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder={
                canEditRemarks ? "The supervisor's note on this row" : ''
              }
            />
            {/* Shown rather than hidden for a recruiter: the note is about
                their row and worth reading, and a field that silently vanished
                by rank would leave them wondering what a manager could see. */}
            <span className="mt-0.5 block text-[11px] text-gray-400">
              {canEditRemarks
                ? 'Also editable in place on the row.'
                : 'Your manager writes this. Read-only for you.'}
            </span>
          </label>
        </div>

        {/* Said before the save, not after: the sheet's wording is the only
            record of what a row like "Working/Drop" originally read. */}
        {statusChanged && row.statusRaw && (
          <p className="rounded bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
            This row still reads <strong>{row.statusRaw}</strong> from the
            tracker sheet. Changing the status drops that wording, because it
            would otherwise caption a status it no longer describes.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * The supervisor's note, edited where it is read.
 *
 * In place rather than in a dialog: it is one short field on a wide table, and
 * the value of a follow-up note is that it can be written while looking at the
 * row it is about.
 *
 * Escape cancels and Enter saves, because a note is one line often enough that
 * reaching for the mouse to confirm it is friction. Shift+Enter still breaks a
 * line, so a longer note is not blocked by that shortcut.
 */
function RemarksCell({
  row,
  editable,
  editing,
  saving,
  onEdit,
  onCancel,
  onSave,
}: {
  row: TrackerRow;
  editable: boolean;
  editing: boolean;
  saving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (note: string) => void;
}) {
  const [draft, setDraft] = useState(row.trackerRemarks ?? '');

  if (!editing) {
    if (!editable) {
      return (
        <span title={row.trackerRemarks ?? undefined}>
          {row.trackerRemarks ?? <Blank />}
        </span>
      );
    }
    return (
      <button
        type="button"
        className="w-full truncate text-left hover:text-gray-900 hover:underline"
        onClick={onEdit}
        title={row.trackerRemarks ?? 'Add a follow-up note'}
      >
        {row.trackerRemarks ?? <Blank />}
      </button>
    );
  }

  return (
    <div className="space-y-1">
      <textarea
        spellCheck={true}
        className="input w-full text-xs"
        rows={2}
        autoFocus
        value={draft}
        maxLength={2000}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onCancel();
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            onSave(draft);
          }
        }}
      />
      <div className="flex gap-1.5">
        <button
          type="button"
          className="btn-primary px-2 py-0.5 text-[11px]"
          disabled={saving}
          onClick={() => onSave(draft)}
        >
          Save
        </button>
        <button
          type="button"
          className="btn-ghost px-2 py-0.5 text-[11px]"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function TrackerPage() {
  const queryClient = useQueryClient();
  const me = useAuth((state) => state.user);
  const [editing, setEditing] = useState<TrackerRow | null>(null);
  const [editingRemarks, setEditingRemarks] = useState<string | null>(null);

  /**
   * Manager and above, matching the server's Approver on the remarks route.
   *
   * The same test also gates the recruiter filter, for a different reason: a
   * recruiter is scoped to their own rows anyway, so the control would be a
   * dropdown with one entry that cannot change anything.
   */
  const supervisor = isSupervisor(me);

  const [recruiterId, setRecruiterId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');

  /**
   * Everyone who could be named as a recruiter on a submission.
   *
   * Not filtered to `role=recruiter`: a manager or company admin who makes a
   * submission is its recruiter too, and filtering the dropdown by role would
   * silently omit them — the filter would then hide rows it should show.
   * Plain employees are dropped because they never own one.
   */
  const staff = useQuery<Page<User>>({
    queryKey: ['users', 'tracker-recruiters'],
    queryFn: async () =>
      (await api.get('/api/v1/admin/users', { params: { limit: 200 } })).data,
    enabled: supervisor,
  });

  const recruiters = (staff.data?.items ?? []).filter((u) => u.role !== 'employee');

  const tracker = useQuery<Page<TrackerRow>>({
    // The acting company is not in the key — the request interceptor puts it on
    // the call, and switching company resets this cache at the picker.
    queryKey: ['tracker', recruiterId, statusFilter, year, month],
    queryFn: async () =>
      (
        await api.get('/api/v1/tracker', {
          params: {
            limit: 200,
            ...(recruiterId ? { recruiterId } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(year ? { year: Number(year) } : {}),
            ...(month ? { month: Number(month) } : {}),
          },
        })
      ).data,
  });

  /**
   * The rows on screen, as a spreadsheet.
   *
   * Sends the same four filters the list query above sends, from the same
   * state, so a filtered view downloads as the filtered rows. The status one
   * matters most: the tracker's Status is derived across submission,
   * interview, offer and placement, and the server filters the export on that
   * same expression rather than on the stored submission status.
   */
  const exportTracker = useMutation({
    mutationFn: async () =>
      downloadExport({
        kind: 'submissions',
        format: 'xlsx',
        params: {
          ...(recruiterId ? { recruiterId } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(year ? { year: Number(year) } : {}),
          ...(month ? { month: Number(month) } : {}),
        },
      }),
    onSuccess: (name) => toast.success(`Saved ${name}`),
    onError: (error) => {
      void exportErrorMessage(error).then((message) => toast.error(message));
    },
  });

  /** Both writes land on the same screen, so both invalidate the same keys. */
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tracker'] });
    // Prefix match, so Placements picks the row up as well as this screen.
    void queryClient.invalidateQueries({ queryKey: ['placements'] });
  };

  /**
   * The dialog saves to two places, because Remarks is not a placement field.
   *
   * Sequential and in this order: the placement fields are the substance of
   * the correction, the note is a comment on it. If the second call fails the
   * first has still landed, and saying so beats a single "Saved" that is
   * mostly true — a rollback across two endpoints is not something this pair
   * can honestly offer.
   *
   * The remarks call is skipped entirely when the text did not change, so the
   * ordinary edit stays one request.
   */
  const savePlacement = useMutation({
    mutationFn: async ({
      placementId,
      submissionId,
      payload,
      remarks,
    }: {
      placementId: string;
      submissionId: string;
      payload: Record<string, unknown>;
      remarks: { changed: boolean; value: string };
    }) => {
      await api.patch(`/api/v1/placements/${placementId}`, payload);
      if (remarks.changed) {
        try {
          await api.patch(`/api/v1/tracker/${submissionId}/remarks`, {
            trackerRemarks: remarks.value || null,
          });
        } catch (error) {
          throw new Error(
            `Row saved, but the remarks could not be updated: ${errorMessage(error)}`,
          );
        }
      }
    },
    onSuccess: () => {
      invalidate();
      setEditing(null);
      toast.success('Saved');
    },
    onError: (error) => {
      // The placement half may well have landed, so the table is refreshed
      // either way rather than left showing values the server no longer holds.
      invalidate();
      toast.error(errorMessage(error));
    },
  });

  const saveRemarks = useMutation({
    mutationFn: async ({
      submissionId,
      note,
    }: {
      submissionId: string;
      note: string;
    }) =>
      (
        await api.patch(`/api/v1/tracker/${submissionId}/remarks`, {
          trackerRemarks: note,
        })
      ).data,
    onSuccess: () => {
      invalidate();
      setEditingRemarks(null);
      toast.success('Note saved');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rows = tracker.data?.items ?? [];
  const total = tracker.data?.total ?? 0;
  const filtered = Boolean(recruiterId || statusFilter || year || month);

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">Tracker</h2>
        </div>
        <p className="mt-1 max-w-2xl text-xs text-gray-500">
          One row per submission, from the day a candidate went to a client
          through to whether they are still there. Status is read across the
          interview, offer and placement behind each row, so it moves on its
          own as the pipeline does.
        </p>

        <div className="mt-3 flex flex-wrap items-end gap-3">
          {supervisor && (
            <label className="text-xs text-gray-600">
              Recruiter
              <select
                className="input mt-1 py-1 text-xs"
                value={recruiterId}
                onChange={(e) => setRecruiterId(e.target.value)}
              >
                <option value="">Everyone</option>
                {recruiters.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.fullName}
                  </option>
                ))}
              </select>
            </label>
          )}

          <label className="text-xs text-gray-600">
            Status
            <select
              className="input mt-1 py-1 text-xs"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">Any status</option>
              {TRACKER_STATUSES.map((value) => (
                <option key={value} value={value}>{label(value)}</option>
              ))}
            </select>
          </label>

          <label className="text-xs text-gray-600">
            Year
            <select
              className="input mt-1 py-1 text-xs"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              <option value="">Any year</option>
              {YEARS.map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          </label>

          <label className="text-xs text-gray-600">
            Month
            <select
              className="input mt-1 py-1 text-xs"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              <option value="">Any month</option>
              {MONTHS.map((name, index) => (
                <option key={name} value={String(index + 1)}>{name}</option>
              ))}
            </select>
          </label>

          {filtered && (
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setRecruiterId('');
                setStatusFilter('');
                setYear('');
                setMonth('');
              }}
            >
              Clear filters
            </button>
          )}

          {/* Beside the filters rather than in the page header, because what
              it downloads is whatever they currently say. */}
          {supervisor && (
            <button
              type="button"
              className="btn-ghost ml-auto text-xs"
              disabled={exportTracker.isPending}
              onClick={() => exportTracker.mutate()}
              title="Downloads the rows matching the filters above"
            >
              {exportTracker.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-3.5 w-3.5" />
              )}
              Excel
            </button>
          )}
        </div>

        {/* Month means the submission date, which is not the joining month.
            Worth saying on screen: a monthly count here will not match the
            billing month for the same candidate. */}
        {(year || month) && (
          <p className="mt-2 text-xs text-gray-400">
            Filed by the date the candidate was submitted, not the date they
            joined — so somebody submitted in February and joined in March is
            in February.
          </p>
        )}
      </section>

      {tracker.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : tracker.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">
            Could not load the tracker. This is a failure to fetch, not an empty
            sheet.
          </p>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => void tracker.refetch()}
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card text-center">
          <Table2 className="mx-auto h-6 w-6 text-gray-300" />
          <p className="mt-2 text-sm text-gray-600">
            {filtered
              ? 'No rows match these filters.'
              : 'Nothing submitted yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-20 border-b border-gray-200 bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  {/* Sr.No is now the first column */}
                  <th scope="col" className="px-4 py-2.5 font-medium" title="Serial Number">Sr.No</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Candidate</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Recruiter</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Mobile</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Client</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Job location</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Designation</th>
                  <th scope="col" className="px-4 py-2.5 font-medium" title="Date of Joining">DOJ</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">CTC</th>
                  {/* Derived from the agreed fee, never typed.
                      Called recruiter_commission on the server, and
                      recruiterCommission on the row — the rename is the
                      business's word for it and stops at the display. See
                      that function for why the base is the fee and not the
                      CTC or the amount received. */}
                  <th scope="col" className="px-4 py-2.5 font-medium" title="4% of the Agreed Fee">
                    Incentive
                  </th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Status</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Payment</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">Remarks</th>
                  <th scope="col" className="px-4 py-2.5 font-medium">
                    <span className="sr-only">Edit</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, index) => (
                  <tr key={row.submissionId} className={ROW_TONE[row.status]}>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-400">
                      {index + 1}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-900">
                      {row.candidateName ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {row.recruiterName ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {formatPhone(row.candidatePhone) ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {row.clientName ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                      {row.location ?? <Blank />}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {row.position ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {row.dateOfJoining ?? <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-900">
                      {row.ctcAnnual ? money(row.ctcAnnual) : <Blank />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-gray-600">
                      {/* Blank, not zero, where no fee has been agreed. A
                          0.00 would assert an incentive of nothing rather
                          than the absence of one. */}
                      {row.recruiterCommission ? (
                        money(row.recruiterCommission)
                      ) : (
                        <Blank />
                      )}
                    </td>
                    <td
                      className={`whitespace-nowrap px-4 py-2.5 font-medium ${STATUS_TEXT[row.status]}`}
                    >
                      {/* What the sheet wrote, when it wrote something.
                          "Working/Drop" normalises to `drop` because a drop
                          outranks working, so showing only the enum would
                          rewrite a real row into something nobody typed. The
                          colour still comes from the enum. */}
                      {row.statusRaw ?? label(row.status)}
                    </td>
                    {/* A simplified reading of the invoice, not a second
                        status. The real draft/raised/partially paid/paid
                        vocabulary is untouched and lives on the invoice
                        screens; this answers "did the money land" for
                        somebody who does not work in billing.

                        Not invoiced is deliberately its own state and reads
                        grey, not amber: nothing is owed on a placement nobody
                        has billed for, and showing it as awaited would have
                        the tracker assert a receivable that does not exist. */}
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <span className={`badge ${PAYMENT_TONE[row.paymentState]}`}>
                        {PAYMENT_LABEL[row.paymentState]}
                      </span>
                    </td>
                    <td className="max-w-xs px-4 py-2.5 text-gray-500">
                      <RemarksCell
                        // Keyed by row, so the draft belongs to the row it was
                        // opened on rather than to the cell's position.
                        key={row.submissionId}
                        row={row}
                        editable={supervisor}
                        editing={editingRemarks === row.submissionId}
                        saving={saveRemarks.isPending}
                        onEdit={() => setEditingRemarks(row.submissionId)}
                        onCancel={() => setEditingRemarks(null)}
                        onSave={(note) =>
                          saveRemarks.mutate({
                            submissionId: row.submissionId,
                            note,
                          })
                        }
                      />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {/* Absent, not disabled, on a row with no placement:
                          every field in the dialog lives on the placement, so
                          there would be nothing for it to write to. */}
                      {row.placementId && (
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          onClick={() => setEditing(row)}
                          title="Correct This Row"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          <span className="sr-only">
                            Edit {row.candidateName ?? 'placement'}
                          </span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* The API caps a page at 200. Saying so beats a table that looks
              complete and is not — and the numbering in column one is a
              position within what is shown, so a truncated view has to say it. */}
          <p className="text-xs text-gray-400">
            {total > rows.length
              ? `Showing ${rows.length} of ${total}. Narrow by recruiter, status or month to see the rest.`
              : `${total} ${total === 1 ? 'row' : 'rows'}.`}
          </p>
        </>
      )}

      {editing && editing.placementId && (
        <EditDialog
          // Keyed by row, so the fields reload instead of keeping the previous
          // row's edits when a second row is opened.
          key={editing.submissionId}
          row={editing}
          saving={savePlacement.isPending}
          canEditRemarks={supervisor}
          onCancel={() => setEditing(null)}
          onSave={(payload, remarks) =>
            savePlacement.mutate({
              placementId: editing.placementId as string,
              submissionId: editing.submissionId,
              payload,
              remarks,
            })
          }
        />
      )}
    </div>
  );
}
